/**
 * Seat-map model + geometry. Ported from the approved prototype's editor (design/gise.html §8)
 * so the editor, the server-side publish validation and the audience seat map all share one geometry.
 *
 * Model: a venue map is a list of sections (rows of seats on a straight line or a concentric arc),
 * plus per-seat overrides keyed `sectionId:row:col`.
 */
import { TIER_KEYS, type Tier } from './pricing';

export type RowDir = 'front' | 'back';
export type Numbering = 'ltr' | 'rtl' | 'oddeven';

export interface Section {
  id: string;
  name: string;
  x: number;
  y: number;
  rot: number;
  sx: number;
  sy: number;
  curve: number;
  stagger: boolean;
  aisles: number[];
  tier: Tier | null;
  rowStart: string;
  skipI: boolean;
  rowDir: RowDir;
  numbering: Numbering;
  rowSeats: number[];
}

export interface SeatOverride {
  t?: Tier | null;
  wc?: boolean;
  comp?: boolean;
  rv?: boolean;
  bl?: boolean;
  rm?: boolean;
}

export interface MapModel {
  secs: Section[];
  ov: Record<string, SeatOverride>;
}

export interface LocalSeat {
  k: string;
  r: number;
  c: number;
  n: number;
  x: number;
  y: number;
  a: number;
  num: number;
  row: string;
}

export interface SectionLayout {
  seats: LocalSeat[];
  lbls: { x: number; y: number; t: string }[];
  b: { x0: number; y0: number; x1: number; y1: number };
  cx: number;
  cy: number;
}

export interface AABB {
  x0: number;
  y0: number;
  x1: number;
  y1: number;
  cx: number;
  cy: number;
}

export const AISLE_W = 24;
export const STAGE_CX = 550;
const ALPH = 'ABCDEFGHIJKLMNOPQRSTUVWXYZ';

export const baseSec = (o: Partial<Section> & Pick<Section, 'id' | 'name' | 'x' | 'y' | 'rowSeats'>): Section => ({
  rot: 0,
  sx: 30,
  sy: 32,
  curve: 0,
  stagger: false,
  aisles: [],
  tier: null,
  rowStart: 'A',
  skipI: false,
  rowDir: 'front',
  numbering: 'ltr',
  ...o,
});

export const rowsOf = (sec: Section) => sec.rowSeats.length;
export const curveR = (c: number) => 260 + Math.pow(100 - c, 2) * 0.6;

export function rowLabel(sec: Section, r: number): string {
  const i = sec.rowDir === 'front' ? r : rowsOf(sec) - 1 - r;
  const st = (sec.rowStart || 'A').trim();
  if (/^\d+$/.test(st)) return String(+st + i);
  if (/^[A-Za-z]$/.test(st)) {
    const a = sec.skipI ? ALPH.replace('I', '') : ALPH;
    const k = a.indexOf(st.toUpperCase()) + i;
    return k < a.length ? a[k]! : a[(k - a.length) % a.length]!.repeat(2);
  }
  return rowsOf(sec) > 1 ? `${st} ${i + 1}` : st;
}

export function seatNums(sec: Section, n: number): number[] {
  const out: number[] = [];
  if (sec.numbering === 'rtl') for (let c = 0; c < n; c++) out[c] = n - c;
  else if (sec.numbering === 'oddeven') {
    const h = Math.ceil(n / 2);
    let k = 1;
    for (let c = h; c < n; c++) {
      out[c] = k;
      k += 2;
    }
    k = 2;
    for (let c = h - 1; c >= 0; c--) {
      out[c] = k;
      k += 2;
    }
  } else for (let c = 0; c < n; c++) out[c] = c + 1;
  return out;
}

export function sectionLayout(sec: Section): SectionLayout {
  const seats: LocalSeat[] = [];
  const lbls: SectionLayout['lbls'] = [];
  const arc = sec.curve > 0;
  const R0 = curveR(sec.curve);
  let x0 = 1e9,
    y0 = 1e9,
    x1 = -1e9,
    y1 = -1e9;
  const ext = (x: number, y: number, p: number) => {
    x0 = Math.min(x0, x - p);
    x1 = Math.max(x1, x + p);
    y0 = Math.min(y0, y - p);
    y1 = Math.max(y1, y + p);
  };
  const map = (u: number, r: number) => {
    if (!arc) return { x: u, y: r * sec.sy, a: 0 };
    const R = R0 + r * sec.sy,
      th = u / R;
    return { x: R * Math.sin(th), y: -R0 + R * Math.cos(th), a: (-th * 180) / Math.PI };
  };
  sec.rowSeats.forEach((n, r) => {
    const splits = new Set(sec.aisles.map((p) => Math.round(p * n)).filter((i) => i > 0 && i < n));
    const pos: number[] = [];
    let s = 0;
    for (let c = 0; c < n; c++) {
      if (c) {
        s += sec.sx;
        if (splits.has(c)) s += AISLE_W;
      }
      pos.push(s);
    }
    const off = -s / 2 + (sec.stagger && r % 2 ? sec.sx / 2 : 0);
    const nums = seatNums(sec, n),
      rl = rowLabel(sec, r);
    for (let c = 0; c < n; c++) {
      const p = map(pos[c]! + off, r);
      seats.push({ k: `${sec.id}:${r}:${c}`, r, c, n, x: p.x, y: p.y, a: p.a, num: nums[c]!, row: rl });
      ext(p.x, p.y, 15);
    }
    if (rowsOf(sec) > 1) {
      const a = map(pos[0]! + off - sec.sx * 0.95, r),
        b = map(pos[n - 1]! + off + sec.sx * 0.95, r);
      lbls.push({ x: a.x, y: a.y, t: rl }, { x: b.x, y: b.y, t: rl });
      ext(a.x, a.y, 8);
      ext(b.x, b.y, 8);
    }
  });
  return { seats, lbls, b: { x0, y0, x1, y1 }, cx: (x0 + x1) / 2, cy: (y0 + y1) / 2 };
}

export function toWorld(sec: Section, L: SectionLayout, lx: number, ly: number, ox = sec.x, oy = sec.y) {
  const a = (sec.rot * Math.PI) / 180,
    dx = lx - L.cx,
    dy = ly - L.cy;
  return { x: ox + L.cx + dx * Math.cos(a) - dy * Math.sin(a), y: oy + L.cy + dx * Math.sin(a) + dy * Math.cos(a) };
}

export function secAABB(sec: Section, L: SectionLayout = sectionLayout(sec), ox = sec.x, oy = sec.y): AABB {
  const b = L.b;
  const pts = [
    [b.x0, b.y0],
    [b.x1, b.y0],
    [b.x0, b.y1],
    [b.x1, b.y1],
  ].map(([x, y]) => toWorld(sec, L, x!, y!, ox, oy));
  const xs = pts.map((p) => p.x),
    ys = pts.map((p) => p.y);
  const x0 = Math.min(...xs),
    x1 = Math.max(...xs),
    y0 = Math.min(...ys),
    y1 = Math.max(...ys);
  return { x0, x1, y0, y1, cx: (x0 + x1) / 2, cy: (y0 + y1) / 2 };
}

export const parseK = (k: string) => {
  const [id, r, c] = k.split(':');
  return { id: id!, r: +r!, c: +c! };
};

export const seatTitle = (sec: Section, s: { row: string; num: number }) =>
  rowsOf(sec) > 1 ? `${sec.name} · ${s.row} sırası · ${s.num}` : `${sec.name} · Koltuk ${s.num}`;

/* ---------------- compiled map (what audiences, the inventory DO and tickets use) ---------------- */

export interface CompiledSeat {
  id: string; // stable key  sectionId:row:col
  sec: string; // section name
  secId: string;
  row: string; // row label ("F", "Loca 1")
  num: number;
  x: number;
  y: number;
  tier: Tier;
  wc: boolean;
  comp: boolean;
  rv: boolean;
  bl: boolean; // not for sale (tech desk, camera)
  single: boolean; // single-row section (loca): label "Loca 1 · Koltuk 3"
  gate: 'A' | 'B';
}

export interface CompiledSection {
  id: string;
  name: string;
  rows: number;
  box: AABB;
  lbls: { x: number; y: number; t: string }[];
}

export interface CompiledMap {
  seats: CompiledSeat[];
  sections: CompiledSection[];
  bbox: { x0: number; y0: number; x1: number; y1: number };
}

export const STAGE = { path: 'M300 70 H800 V110 Q550 150 300 110Z', tx: 550, ty: 94, x0: 300, y0: 70, x1: 800, y1: 140 };

/** Compile an editor model. Removed seats are dropped; unpriced seats are dropped (checks flag them). */
export function compileMap(model: MapModel): CompiledMap {
  const seats: CompiledSeat[] = [];
  const sections: CompiledSection[] = [];
  let bx0 = STAGE.x0,
    by0 = STAGE.y0,
    bx1 = STAGE.x1,
    by1 = STAGE.y1;
  for (const sec of model.secs) {
    const L = sectionLayout(sec);
    const box = secAABB(sec, L);
    bx0 = Math.min(bx0, box.x0);
    by0 = Math.min(by0, box.y0);
    bx1 = Math.max(bx1, box.x1);
    by1 = Math.max(by1, box.y1);
    sections.push({
      id: sec.id,
      name: sec.name,
      rows: rowsOf(sec),
      box,
      lbls: L.lbls.map((l) => ({ ...toWorld(sec, L, l.x, l.y), t: l.t })),
    });
    for (const s of L.seats) {
      const o = model.ov[s.k] || {};
      if (o.rm) continue;
      const t = o.t !== undefined ? o.t : sec.tier;
      if (!t) continue;
      const w = toWorld(sec, L, s.x, s.y);
      seats.push({
        id: s.k,
        sec: sec.name,
        secId: sec.id,
        row: s.row,
        num: s.num,
        x: Math.round(w.x * 10) / 10,
        y: Math.round(w.y * 10) / 10,
        tier: t,
        wc: !!o.wc,
        comp: !!o.comp,
        rv: !!o.rv,
        bl: !!o.bl,
        single: rowsOf(sec) === 1,
        gate: w.x < STAGE_CX ? 'A' : 'B',
      });
    }
  }
  return { seats, sections, bbox: { x0: bx0, y0: by0, x1: bx1, y1: by1 } };
}

export const seatLabel = (s: Pick<CompiledSeat, 'sec' | 'row' | 'num' | 'single'>, lang: 'tr' | 'en' = 'tr') =>
  s.single
    ? `${s.row} · ${lang === 'en' ? 'Seat' : 'Koltuk'} ${s.num}`
    : lang === 'en'
      ? `${s.sec} · Row ${s.row} · ${s.num}`
      : `${s.sec} · ${s.row} sırası · ${s.num}`;

/* ---------------- stats & pre-publish checks (shared by editor UI and server) ---------------- */

export interface MapStats {
  active: number;
  blocked: number;
  wc: number;
  comp: number;
  rv: number;
  none: number;
  rm: number;
  tiers: Record<Tier, number>;
  keys: Set<string>;
  noneKeys: string[];
  wcKeys: string[];
  rvKeys: string[];
  per: Record<string, number>;
  sellable: number;
  orphans: string[];
  rev: number;
}

export function mapStats(model: MapModel, locked: Set<string>, prices?: Partial<Record<Tier, number>>): MapStats {
  const st: MapStats = {
    active: 0,
    blocked: 0,
    wc: 0,
    comp: 0,
    rv: 0,
    none: 0,
    rm: 0,
    tiers: { loca: 0, on: 0, arka: 0, balkon: 0 },
    keys: new Set(),
    noneKeys: [],
    wcKeys: [],
    rvKeys: [],
    per: {},
    sellable: 0,
    orphans: [],
    rev: 0,
  };
  for (const sec of model.secs) {
    let n = 0;
    for (const s of sectionLayout(sec).seats) {
      const o = model.ov[s.k] || {};
      if (o.rm) {
        st.rm++;
        continue;
      }
      st.keys.add(s.k);
      st.active++;
      n++;
      const t = o.t !== undefined ? o.t : sec.tier;
      if (o.bl) st.blocked++;
      if (!t) {
        st.none++;
        st.noneKeys.push(s.k);
      } else {
        st.tiers[t]++;
        if (!o.bl && prices) st.rev += prices[t] ?? 0;
      }
      if (o.wc) {
        st.wc++;
        st.wcKeys.push(s.k);
      }
      if (o.comp) st.comp++;
      if (o.rv) {
        st.rv++;
        st.rvKeys.push(s.k);
      }
    }
    st.per[sec.id] = n;
  }
  st.sellable = st.active - st.blocked;
  st.orphans = [...locked].filter((k) => !st.keys.has(k));
  return st;
}

export function mapOverlaps(model: MapModel): [Section, Section][] {
  const out: [Section, Section][] = [];
  const bx = model.secs.map((s) => [s, secAABB(s)] as const);
  for (let i = 0; i < bx.length; i++)
    for (let j = i + 1; j < bx.length; j++) {
      const a = bx[i]![1],
        b = bx[j]![1],
        m = 6;
      if (a.x0 + m < b.x1 && b.x0 + m < a.x1 && a.y0 + m < b.y1 && b.y0 + m < a.y1) out.push([bx[i]![0], bx[j]![0]]);
    }
  return out;
}

export type CheckLevel = 'err' | 'warn' | 'ok' | 'info';
export interface MapCheck {
  l: CheckLevel;
  code: string;
  t: string;
  d: string;
  keys?: string[];
  secs?: string[];
}

/** Server-authoritative publish checks. Errors block publishing. */
export function mapChecks(model: MapModel, locked: Set<string>, license: number, st = mapStats(model, locked)): MapCheck[] {
  const c: MapCheck[] = [];
  const ovOf = (k: string) => model.ov[k] || {};
  const over = st.sellable - license;
  c.push(
    over > 0
      ? { l: 'err', code: 'capacity', t: 'Ruhsat kapasitesi aşılıyor', d: `Satışa açık ${st.sellable} koltuk var, ruhsat ${license}. ${over} koltuğu kaldır ya da satışa kapat.` }
      : { l: 'ok', code: 'capacity', t: 'Kapasite ruhsata uygun', d: `Satışa açık ${st.sellable} / ${license} koltuk${st.blocked ? ` · ${st.blocked} koltuk satışa kapalı` : ''}` },
  );
  if (st.orphans.length)
    c.push({
      l: 'err',
      code: 'orphans',
      t: `${st.orphans.length} satılmış koltuk haritadan çıkıyor`,
      d: 'Bu koltukların biletleri yaklaşan seanslarda satıldı. Koltukları geri getir ya da değişikliği geri al.',
      keys: st.orphans,
    });
  const lockedBlocked = [...locked].filter((k) => st.keys.has(k) && ovOf(k).bl);
  if (lockedBlocked.length)
    c.push({ l: 'err', code: 'locked_blocked', t: `${lockedBlocked.length} satılmış koltuk satışa kapatılmış`, d: 'Satılmış koltuk satışa kapatılamaz.', keys: lockedBlocked });
  if (st.none) c.push({ l: 'err', code: 'unpriced', t: `${st.none} koltuğun fiyatı yok`, d: 'Fiyat kategorisi atanmamış koltuklar satışa çıkamaz.', keys: st.noneKeys });
  for (const [a, b] of mapOverlaps(model))
    c.push({ l: 'err', code: 'overlap', t: `${a.name} ile ${b.name} üst üste`, d: 'Bölümler çakışıyor. Birini taşı ya da döndür.', secs: [a.id, b.id] });
  const lone = st.wcKeys.filter((k) => {
    const { id, r, c: cc } = parseK(k);
    const L = ovOf(`${id}:${r}:${cc - 1}`),
      R = ovOf(`${id}:${r}:${cc + 1}`);
    return !L.comp && !R.comp && !L.wc && !R.wc;
  });
  if (st.wc < 4) c.push({ l: 'warn', code: 'wc_low', t: 'Erişilebilir alan az', d: `${st.wc} tekerlekli sandalye alanı var. Bu salon için hedef en az 4.` });
  else c.push({ l: 'ok', code: 'wc', t: `${st.wc} erişilebilir alan`, d: `${st.comp} refakatçi koltuğu ile` });
  if (lone.length)
    c.push({ l: 'warn', code: 'wc_lone', t: `${lone.length} erişilebilir alanın yanında refakatçi koltuğu yok`, d: 'Yanındaki koltuğu refakatçi olarak işaretle.', keys: lone });
  const names = model.secs.map((s) => s.name.trim().toLocaleLowerCase('tr-TR'));
  const dup = [...new Set(names.filter((n, i) => names.indexOf(n) !== i))];
  if (dup.length) c.push({ l: 'warn', code: 'dup_names', t: 'Aynı adlı bölümler var', d: `"${dup.join('", "')}" birden fazla. Biletlerde karışıklık olur.` });
  if (st.rv) c.push({ l: 'info', code: 'rv', t: `${st.rv} kısıtlı görüşlü koltuk`, d: 'Seyirciye seçimden önce “kısıtlı görüş” notu gösterilir.', keys: st.rvKeys });
  return c;
}

export const isTier = (t: unknown): t is Tier => typeof t === 'string' && (TIER_KEYS as readonly string[]).includes(t);

/** Structural validation of an untrusted model (from the editor). Throws on garbage. */
export function sanitizeModel(input: unknown): MapModel {
  const m = input as MapModel;
  if (!m || !Array.isArray(m.secs) || typeof m.ov !== 'object' || m.ov === null) throw new Error('invalid_model');
  if (m.secs.length > 40) throw new Error('too_many_sections');
  const ids = new Set<string>();
  const secs = m.secs.map((s) => {
    if (typeof s.id !== 'string' || !/^[a-z0-9_-]{1,24}$/i.test(s.id) || ids.has(s.id)) throw new Error('invalid_section_id');
    ids.add(s.id);
    if (!Array.isArray(s.rowSeats) || !s.rowSeats.length || s.rowSeats.length > 60) throw new Error('invalid_rows');
    const num = (v: unknown, lo: number, hi: number, d: number) => (typeof v === 'number' && isFinite(v) ? Math.max(lo, Math.min(hi, v)) : d);
    return baseSec({
      id: s.id,
      name: String(s.name ?? '').slice(0, 40) || s.id,
      x: num(s.x, -5000, 5000, 0),
      y: num(s.y, -5000, 5000, 0),
      rot: num(s.rot, -180, 180, 0),
      sx: num(s.sx, 20, 60, 30),
      sy: num(s.sy, 20, 80, 32),
      curve: num(s.curve, 0, 100, 0),
      stagger: !!s.stagger,
      aisles: (Array.isArray(s.aisles) ? s.aisles : []).filter((p) => typeof p === 'number' && p > 0 && p < 1).slice(0, 8),
      tier: isTier(s.tier) ? s.tier : null,
      rowStart: String(s.rowStart ?? 'A').slice(0, 16),
      skipI: !!s.skipI,
      rowDir: s.rowDir === 'back' ? 'back' : 'front',
      numbering: s.numbering === 'rtl' || s.numbering === 'oddeven' ? s.numbering : 'ltr',
      rowSeats: s.rowSeats.map((n) => num(Math.round(n), 1, 60, 1)),
    });
  });
  const ov: Record<string, SeatOverride> = {};
  for (const [k, o] of Object.entries(m.ov)) {
    if (!/^[a-z0-9_-]{1,24}:\d{1,2}:\d{1,2}$/i.test(k) || !o || typeof o !== 'object') continue;
    const clean: SeatOverride = {};
    if ('t' in o) clean.t = isTier(o.t) ? o.t : null;
    for (const f of ['wc', 'comp', 'rv', 'bl', 'rm'] as const) if (o[f]) clean[f] = true;
    ov[k] = clean;
  }
  return { secs, ov };
}
